import type { FC, ReactElement } from 'react';
import { memo } from 'react';
import { Link } from '@compat/router';
import { Picture } from '@ui';
import UIicons from '@ui-kit';
import type { BreadCrumbsT } from '@routes/ProductPage/index.type';
import clsx from 'clsx';
import JsonLd from '@utils/jsonLd/JsonLd';
import { buildBreadcrumbList } from '@utils/jsonLd/breadcrumbs';
import './index.scss';

const { chevronLeftRed } = UIicons;

interface BreadcrumbsProps {
  isDesktop?: boolean;
  array: BreadCrumbsT;
  isShowLinkLastElement?: boolean;
  withoutBackBtn?: boolean;
  noPaddingMobile?: boolean;
}

const hasValidUrl = (url: string | undefined): boolean => !!url?.trim?.();

const normalizeUrlForNavigation = (url: string | undefined): string => {
  if (!hasValidUrl(url)) return '/';
  const trimmed =
    (url as string).slice(-1) === '/'
      ? (url as string).slice(0, -1)
      : (url as string);
  return trimmed || '/';
};

const Breadcrumbs: FC<BreadcrumbsProps> = ({
  isDesktop,
  array,
  isShowLinkLastElement,
  withoutBackBtn,
  noPaddingMobile
}) => {
  let group: BreadCrumbsT = [];

  const filteredArray = array.filter(
    item =>
      item?.title &&
      typeof item?.title === 'string' &&
      item?.title.trim() !== ''
  );
  const breadLength = filteredArray.reduce(
    (acc, item) => acc + (item.title?.length || 0),
    0
  );
  const breadLastEl = filteredArray.length - 1;

  const renderBreadcrumbItem = (
    item: BreadCrumbsT[0],
    position: number,
    isLastInChain: boolean,
    options?: {
      titleOverride?: string;
      linkClassName?: string;
      useSpan?: boolean;
    }
  ): ReactElement => {
    const shouldShowLink =
      hasValidUrl(item.url) && (!isLastInChain || isShowLinkLastElement);
    const displayTitle = options?.titleOverride || item.title;
    const linkClassName = options?.linkClassName ?? 'w-max';

    return (
      <li key={position} data-testid='breadcrumb-item'>
        {shouldShowLink ? (
          <Link
            className={linkClassName}
            to={normalizeUrlForNavigation(item.url)}
          >
            <span>{displayTitle}</span>
          </Link>
        ) : options?.useSpan ? (
          <span>
            <span>{displayTitle}</span>
          </span>
        ) : (
          <div className={linkClassName}>
            <span>{displayTitle}</span>
          </div>
        )}
        {!isLastInChain && <span className='icon' />}
      </li>
    );
  };

  return (
    <div
      className={clsx(
        `breadcrumb`,
        !withoutBackBtn && ' with-back-btn',
        noPaddingMobile && ' breadcrumb--no-padding-mobile'
      )}
    >
      <JsonLd data={buildBreadcrumbList(filteredArray)} />
      <ul className='custom-scroll '>
        {filteredArray?.reduce<ReactElement[]>((acc, item, index) => {
          if (item.binding === true) {
            group.push(item);

            if (breadLastEl !== index && filteredArray[index + 1]?.binding) {
              return acc;
            }
          }

          if (
            (breadLastEl === index ||
              filteredArray[index + 1]?.binding !== true) &&
            group.length > 0
          ) {
            const mapGroup = group;
            group = [];
            const position = acc.length + 1;
            const isLastInChain = index === breadLastEl;
            acc.push(
              <li key={position} data-testid='breadcrumb-item'>
                {mapGroup?.map((a, i) => (
                  <Link
                    key={`link-${a.title || i}`}
                    to={normalizeUrlForNavigation(a.url)}
                  >
                    <span>
                      {a.title}
                      {i !== mapGroup.length - 1 ? (
                        <span className='comma'>, </span>
                      ) : (
                        ''
                      )}
                    </span>
                  </Link>
                ))}
                {!isLastInChain && <span className='icon' />}
              </li>
            );
            return acc;
          }

          const position = acc.length + 1;
          const isLastInChain = index === breadLastEl;

          if (isDesktop && breadLastEl === index && breadLength > 150) {
            acc.push(
              renderBreadcrumbItem(item, position, isLastInChain, {
                titleOverride: `${item.title?.slice(0, 50)}...`,
                linkClassName: '',
                useSpan: true
              })
            );
            return acc;
          }

          acc.push(renderBreadcrumbItem(item, position, isLastInChain));
          return acc;
        }, [])}
      </ul>
      {!withoutBackBtn && (
        <Link to='/catalog' className='go-back-btn'>
          <Picture
            className='go-back-btn__icon'
            height='24'
            icon={chevronLeftRed}
          />
          <span className='go-back-btn__text'>В каталог</span>
        </Link>
      )}
    </div>
  );
};

export default memo(Breadcrumbs);
